import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import ThumbStatusIndicator from '@wix/da-ds/pkg/thumbs/ThumbStatusIndicator';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import UserLink from '@wix/da-shared-react/pkg/User/Link';
import { formatDollars as baseFormatDollars } from '@wix/da-shared-react/pkg/utils/formatting';

import s from './DeviationSaleIndicators.scss';

interface Props {
  deviation: PapiDeviation;
}

// Format prices with optional cents and separating thousands with a comma
const formatDollars = (amount: number | undefined) =>
  baseFormatDollars(amount, { useGrouping: true });

const DeviationSaleIndicators: FC<Props> = ({ deviation }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { user } = useContext(SessionContext);
  const { username } = user as PapiUser;

  const { onSaleAs = [] } = deviation;
  const showTierIndicator = onSaleAs.includes('in_tier');
  const showPremiumGalleryIndicator = onSaleAs.includes('premium_gallery');
  const showPcpIndicator = onSaleAs.includes('pcp');
  const showExclusiveIndicator = onSaleAs.includes('exclusive');

  if (isMobile) {
    return (
      <div className={s['root']}>
        {showTierIndicator && (
          <ThumbStatusIndicator type="in-tier" size="small" hasAccess />
        )}
        {showPremiumGalleryIndicator && (
          <ThumbStatusIndicator type="premium-folder" size="small" />
        )}
        {showPcpIndicator && <ThumbStatusIndicator type="pcp" size="small" />}
        {showExclusiveIndicator && (
          <ThumbStatusIndicator type="adoptable" size="small" />
        )}
      </div>
    );
  }

  const tierPrice = deviation.primaryTier?.tier?.dollarPrice;
  const exclusive = showExclusiveIndicator && deviation.adoptable;
  const isOwner = exclusive && exclusive.owner?.username === username;
  const isExclusiveSold = exclusive && exclusive.status === 'sold';
  const isExclusiveOnSale = exclusive && exclusive.status === 'onsale';
  const exclusiveTooltipText = exclusive && (
    <>
      {[
        isExclusiveSold && (
          <div key="soldFor">
            {t('studio.table.sellIndicators.exclusive.tooltip.soldFor', {
              price: formatDollars(
                exclusive.fixedPrice?.fixedPrice || exclusive.offers?.offerPrice
              ),
            })}
          </div>
        ),

        exclusive.owner && !isOwner && (
          <div key="owner">
            {t('studio.table.sellIndicators.exclusive.tooltip.owner', {
              postProcess: ['reactPostprocessor', 'featureBrancher'],
              userLink: (
                <UserLink className={s['link']} user={exclusive.owner} />
              ),
            })}
          </div>
        ),

        isExclusiveOnSale && exclusive.owner && !isOwner && (
          <div key="resell">
            {t('studio.table.sellIndicators.exclusive.tooltip.resell')}
          </div>
        ),

        isExclusiveOnSale && exclusive.offers?.offerPrice && (
          <div key="startingPrice">
            {t('studio.table.sellIndicators.exclusive.tooltip.startingPrice', {
              price: formatDollars(exclusive.offers.offerPrice),
            })}
          </div>
        ),

        isExclusiveOnSale && exclusive.fixedPrice?.fixedPrice && (
          <div key="fixedPrice">
            {t('studio.table.sellIndicators.exclusive.tooltip.fixedPrice', {
              price: formatDollars(exclusive.fixedPrice.fixedPrice),
            })}
          </div>
        ),
      ].filter(t => t)}
    </>
  );

  return (
    <div className={s['root']} onClick={e => e.stopPropagation()}>
      {showTierIndicator && (
        <SimpleTooltip
          text={t('studio.table.sellIndicators.tier.tooltip', {
            postProcess: 'reactPostprocessor',
            tierName: (
              <a
                className={s['link']}
                target="_blank"
                rel="noopener noreferrer"
                href={deviation.primaryTier?.url}
              >
                {deviation.primaryTier?.title}
              </a>
            ),
            price: tierPrice && formatDollars(parseFloat(tierPrice)),
          })}
          usePortal
        >
          <ThumbStatusIndicator type="in-tier" size="small" hasAccess />
        </SimpleTooltip>
      )}
      {showPremiumGalleryIndicator && (
        <SimpleTooltip
          text={t('studio.table.sellIndicators.premiumGallery.tooltip', {
            postProcess: 'reactPostprocessor',
            galleryName: (
              <a
                className={s['link']}
                target="_blank"
                rel="noopener noreferrer"
                href={deviation.premiumFolderData?.galleryUrl}
              >
                {deviation.premiumFolderData?.galleryName}
              </a>
            ),
            price: formatDollars(deviation.premiumFolderData?.dollarPrice),
          })}
          usePortal
        >
          <ThumbStatusIndicator type="premium-folder" size="small" />
        </SimpleTooltip>
      )}
      {showPcpIndicator && (
        <SimpleTooltip
          text={t('studio.table.sellIndicators.pcp.tooltip', {
            price: formatDollars(deviation.pcp?.downloadDollarPrice),
          })}
          usePortal
        >
          <ThumbStatusIndicator type="pcp" size="small" />
        </SimpleTooltip>
      )}
      {showExclusiveIndicator && (
        <SimpleTooltip text={exclusiveTooltipText} usePortal>
          <ThumbStatusIndicator type="adoptable" size="small" />
        </SimpleTooltip>
      )}
    </div>
  );
};
DeviationSaleIndicators.displayName = 'DeviationSaleIndicators';

export default DeviationSaleIndicators;
